import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PortalPage, EmptyState, Pill, Section } from "../../components/portal/PortalPage";
import { ProgressStepper } from "../../components/portal/ProgressStepper";
import { usePortal } from "../../components/portal/usePortal";
import { fmtDate, fmtMoney, PROJECT_STATUS, REQUEST_STATUS } from "../../components/portal/portalUtils";
import { Kanban, Plus, MagnifyingGlass, MapPin, CalendarBlank, ArrowRight } from "@phosphor-icons/react";
import { CardListSkeleton } from "../../components/Skeletons";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";

export function ProjectProgressBar({ progress, compact = false }) {
  const pct = progress?.pct ?? 0;
  return (
    <div data-testid="project-progress-bar">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
        <span>{progress?.completed ?? 0} of {progress?.total ?? 0} tasks complete{progress?.in_progress ? ` · ${progress.in_progress} in progress` : ""}</span>
        <span className="font-semibold text-foreground">{pct}%</span>
      </div>
      <div className={`${compact ? "h-1.5" : "h-2.5"} w-full rounded-full bg-muted overflow-hidden`}>
        <div className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? "bg-emerald-500" : "bg-primary"}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
    </div>
  );
}

export default function ClientProjectsPage() {
  const { perms } = usePortal();
  const [view, setView] = useState("active");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    API.get(`/client-hub/projects?view=${view}`).then(({ data }) => setData(data)).catch(() => setData({ projects: [], requests: [] }));
  }, [view]);

  const fam = useFamilyFilter();
  const projects = (data?.projects || []).filter((p) => fam.matches(p) && (!q || `${p.reference} ${p.name} ${p.address}`.toLowerCase().includes(q.toLowerCase())));
  const requests = (data?.requests || []).filter(fam.matches);

  return (
    <PortalPage
      title="Projects"
      subtitle="Larger works and refurbishments — propose a project, approve the quote, then follow every task through to completion."
      testid="client-projects"
      actions={<><FamilyFilter fam={fam} testid="projects-client-filter" />{perms.can_request && <Button size="sm" onClick={() => navigate("/portal/projects/new")} data-testid="new-project-btn"><Plus size={14} className="mr-1" /> New Project</Button>}</>}
    >
      {view === "active" && requests.length > 0 && (
        <Section title={`Project requests in progress (${requests.length})`} className="mb-6" testid="project-requests-section">
          <div className="space-y-2">
            {requests.map((r) => (
              <Link key={r.id} to={`/portal/requests/${r.id}`} className="flex flex-col md:flex-row md:items-center gap-3 border border-border rounded-md p-3 hover:border-primary/40 card-lift" data-testid={`project-request-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-[10px] font-mono text-muted-foreground">{r.reference}</span>
                    <Pill map={REQUEST_STATUS} value={r.status} />
                    {r.estimated_budget > 0 && <span className="text-[10px] text-muted-foreground">~{fmtMoney(r.estimated_budget)}</span>}
                  </div>
                  <p className="text-sm font-semibold truncate">{r.title}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{r.address}{r.desired_start_date ? ` · start from ${fmtDate(r.desired_start_date)}` : ""}</p>
                </div>
                <div className="md:w-[42%] shrink-0"><ProgressStepper progress={r.progress} compact /></div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between mb-4">
        <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
          {[["active", "Live"], ["closed", "Completed"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`px-3 py-1 text-xs rounded ${view === k ? "bg-card shadow font-semibold" : "text-muted-foreground"}`} data-testid={`projects-view-${k}`}>{l}</button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects…" className="pl-8 h-8 text-xs" data-testid="projects-search" />
        </div>
      </div>

      {data === null ? <CardListSkeleton rows={3} showHeader={false} /> : projects.length === 0 ? (
        <EmptyState icon={Kanban} title={view === "active" ? "No live projects yet" : "No projects found"}
          text="Propose a project and we'll quote it. Once you approve, it becomes a live project you can follow here task by task."
          action={perms.can_request && <Button size="sm" onClick={() => navigate("/portal/projects/new")}>Propose a Project</Button>} />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="projects-list">
          {projects.map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} className="block border border-border rounded-lg bg-card p-4 hover:border-primary/40 card-lift" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <span className="text-[10px] font-mono text-muted-foreground flex items-center gap-1.5">{p.reference} <ForClientChip name={p.for_client} clientId={p.client_id} /></span>
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                </div>
                <Pill map={PROJECT_STATUS} value={p.status} />
              </div>
              <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1 mb-3"><MapPin size={11} /> {p.address || "—"}</p>
              <ProjectProgressBar progress={p.progress} compact />
              <div className="flex items-center justify-between mt-3 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><CalendarBlank size={11} /> {p.start_date ? fmtDate(p.start_date) : "TBC"}{p.target_date ? ` → ${fmtDate(p.target_date)}` : ""}</span>
                <span className="flex items-center gap-1 text-primary">View <ArrowRight size={11} /></span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
